import type { SupportTicketSort } from "@redotech/effect-api-shared/schema/query-options";
import {
  decodeSupportTicketFilterGroup,
  encodeSupportTicketFilterGroup,
  supportTicketFilterData,
  SupportTicketFilterNames,
} from "@redotech/effect-api-shared/schema/support-ticket-filters";
import type { ArrayStringFilterData } from "@redotech/redo-model/views/advanced-filters/array-string-filter";
import { FilterOperator } from "@redotech/redo-model/views/advanced-filters/generic-advanced-filter-data";
import { filterGroupsAreEqual } from "@redotech/redo-model/views/utils/util";
import type { FilterGroup } from "@redotech/redo-model/views/views";
import { SPAM_TAG_EXCLUSION_FILTER } from "./default-views";

export const URL_FILTER_GROUP_PARAM = "fg";

export interface SupportTicketQueryOptions {
  filterGroup: FilterGroup;
  sorts: SupportTicketSort[];
  limit?: number;
}

const DEFAULT_STATUS_FILTER: ArrayStringFilterData = {
  ...(supportTicketFilterData[
    SupportTicketFilterNames.STATUS
  ] as ArrayStringFilterData),
  value: ["OPEN"],
};

export const DEFAULT_FILTER_GROUP: FilterGroup = {
  operator: FilterOperator.AND,
  clauses: [DEFAULT_STATUS_FILTER, SPAM_TAG_EXCLUSION_FILTER],
};

const DEFAULT_SORT: SupportTicketSort = {
  kind: "lastCustomerMessageAt",
  direction: "desc",
  nulls: "last",
};

export const DEFAULT_QUERY_OPTIONS: SupportTicketQueryOptions = {
  filterGroup: DEFAULT_FILTER_GROUP,
  sorts: [DEFAULT_SORT],
};

export function parseSupportTicketQueryOptionsFromSearch(
  search: string,
): SupportTicketQueryOptions {
  const queryParams = new URLSearchParams(search);
  const fgParam = queryParams.get(URL_FILTER_GROUP_PARAM);

  if (!fgParam) {
    return DEFAULT_QUERY_OPTIONS;
  }

  try {
    const filterGroup = decodeSupportTicketFilterGroup(fgParam);

    if (!filterGroup) {
      return DEFAULT_QUERY_OPTIONS;
    }

    return { filterGroup, sorts: DEFAULT_QUERY_OPTIONS.sorts };
  } catch {
    return DEFAULT_QUERY_OPTIONS;
  }
}

export function supportTicketQueryOptionsAreEqual(
  a: SupportTicketQueryOptions,
  b: SupportTicketQueryOptions,
): boolean {
  return (
    filterGroupsAreEqual(a.filterGroup, b.filterGroup) &&
    JSON.stringify(a.sorts) === JSON.stringify(b.sorts)
  );
}

export function buildSearchWithSupportTicketQueryOptions(
  currentSearch: string,
  queryOptions: SupportTicketQueryOptions,
): string {
  const searchParams = new URLSearchParams(currentSearch);

  if (supportTicketQueryOptionsAreEqual(queryOptions, DEFAULT_QUERY_OPTIONS)) {
    searchParams.delete(URL_FILTER_GROUP_PARAM);
  } else {
    searchParams.set(
      URL_FILTER_GROUP_PARAM,
      encodeSupportTicketFilterGroup(queryOptions.filterGroup),
    );
  }

  searchParams.delete("defaultView");

  const newSearch = searchParams.toString();
  return newSearch ? `?${newSearch}` : "";
}

export function encodeSupportTicketQueryOptionsForRequest(
  queryOptions: SupportTicketQueryOptions,
): string {
  return encodeSupportTicketFilterGroup(queryOptions.filterGroup);
}
